import { parseISO } from 'date-fns'
import { MedicoesAtendimentosAnterioresQuery } from 'graphql/types.generated'
import { calcularIMC } from 'util/atendimento'
import { dateAsYyyyMmDd } from 'util/date/formatDate'
import { formatNumber, parseNumber } from 'util/number'
import { MedicoesPanelModel } from 'view/atendimentos/components/MedicoesPanel/MedicoesPanel'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { MedicaoAnteriorFormModel } from './MedicaoAnteriorForm'
import { MedicoesRetificacaoQueryModel } from './model-medicoes'

export function convertMedicaoAtendimentoAtual(
  medicaoAtendimento: MedicoesPanelModel,
  dataAtendimento: Instant
): HistoricoMedicaoModel {
  return {
    dataMedicao: dataAtendimento,
    valorPeso: medicaoAtendimento.peso && parseNumber(medicaoAtendimento.peso),
    valorAltura: medicaoAtendimento.altura && parseNumber(medicaoAtendimento.altura),
    valorPerimetroCefalico: medicaoAtendimento.perimetroCefalico && parseNumber(medicaoAtendimento.perimetroCefalico),
    valorCircunferenciaAbdominal:
      medicaoAtendimento.circunferenciaAbdominal && parseNumber(medicaoAtendimento.circunferenciaAbdominal),
    perimetroPanturrilha:
      medicaoAtendimento.perimetroPanturrilha && parseNumber(medicaoAtendimento.perimetroPanturrilha),
    valorImc: getIMCFromLocalizedNumbers(medicaoAtendimento),
    tipoGlicemia: medicaoAtendimento.tipoGlicemia,
    valorGlicemia: medicaoAtendimento.glicemia && parseNumber(medicaoAtendimento.glicemia),
    valorFrequenciaCardiaca:
      medicaoAtendimento.frequenciaCardiaca && parseNumber(medicaoAtendimento.frequenciaCardiaca),
    valorFrequenciaRespiratoria:
      medicaoAtendimento.frequenciaRespiratoria && parseNumber(medicaoAtendimento.frequenciaRespiratoria),
    valorPressaoArterial:
      medicaoAtendimento.pressaoArterialSistolica && medicaoAtendimento.pressaoArterialDiastolica
        ? `${medicaoAtendimento.pressaoArterialSistolica}/${medicaoAtendimento.pressaoArterialDiastolica}`
        : undefined,
    valorSaturacaoO2: medicaoAtendimento.saturacaoO2 && parseNumber(medicaoAtendimento.saturacaoO2),
    valorTemperatura: medicaoAtendimento.temperatura && parseNumber(medicaoAtendimento.temperatura),
    origem: 'atendimentoAtual',
  }
}

export function convertMedicaoAnterior(medicaoAnterior: MedicaoAnteriorFormModel): HistoricoMedicaoModel {
  return {
    id: medicaoAnterior.id,
    dataMedicao: parseISO(medicaoAnterior.data),
    valorPeso: medicaoAnterior.peso && parseNumber(medicaoAnterior.peso),
    valorAltura: medicaoAnterior.altura && parseNumber(medicaoAnterior.altura),
    valorPerimetroCefalico: medicaoAnterior.perimetroCefalico && parseNumber(medicaoAnterior.perimetroCefalico),
    valorImc: getIMCFromLocalizedNumbers(medicaoAnterior),
    origem: 'medicoesAnteriores',
  }
}

export const convertMedicaoAnteriorRetificacao = (medicaoAnterior: MedicaoAnteriorFormModel): HistoricoMedicaoModel => {
  return {
    id: medicaoAnterior.id,
    dataMedicao: medicaoAnterior.data,
    valorPeso: medicaoAnterior.peso && parseNumber(medicaoAnterior.peso),
    valorAltura: medicaoAnterior.altura && parseNumber(medicaoAnterior.altura),
    valorPerimetroCefalico: medicaoAnterior.perimetroCefalico && parseNumber(medicaoAnterior.perimetroCefalico),
    valorImc: getIMCFromLocalizedNumbers(medicaoAnterior),
    origem: 'medicoesAnteriores',
  }
}

export function convertMedicaoOutrosAtendimentos(
  medicao: MedicoesAtendimentosAnterioresQuery['medicoes'][0]
): HistoricoMedicaoModel {
  return {
    ...medicao,
    valorAlturaUterina: Number(medicao.valorAlturaUterina),
    dataRegistro: medicao.atendimentoProfissional.iniciadoEm,
    nomeResponsavel: medicao.atendimentoProfissional.lotacao.profissional.nome,
    origem: 'outroAtendimento',
  }
}

export const convertMedicoesAnterioresQueryRetificacaoToForm = (
  medicoes: MedicoesRetificacaoQueryModel[]
): MedicaoAnteriorFormModel[] => {
  return medicoes
    .sort((a, b) => b.dataMedicao - a.dataMedicao)
    .map((medicao) => ({
      id: medicao.id,
      data: dateAsYyyyMmDd(medicao.dataMedicao),
      peso: medicao.valorPeso ? formatNumber(medicao.valorPeso, 3, false) : undefined,
      altura: medicao.valorAltura ? formatNumber(medicao.valorAltura, 1, false) : undefined,
      perimetroCefalico: medicao.valorPerimetroCefalico
        ? formatNumber(medicao.valorPerimetroCefalico, 1, false)
        : undefined,
      medicoes: {
        peso: medicao.valorPeso,
        altura: medicao.valorAltura,
        perimetroCefalico: medicao.valorPerimetroCefalico,
      },
    }))
}

export function convertMedicoesNoNascimento(
  antecedente: MedicoesAtendimentosAnterioresQuery['medicoesNoNascimento'],
  nascimentoCidadao: LocalDate
): HistoricoMedicaoModel {
  return {
    dataMedicao: nascimentoCidadao,
    valorPeso: antecedente.peso ? Number(antecedente.peso) : undefined,
    valorAltura: antecedente.altura ? Number(antecedente.altura) : undefined,
    valorPerimetroCefalico: antecedente.perimetroCefalico ? Number(antecedente.perimetroCefalico) : undefined,
    valorImc: getIMC(antecedente),
    origem: 'antecedente',
    dataRegistro: antecedente.dataUltimaAtualizacao,
  }
}

const getIMCFromLocalizedNumbers = ({ peso, altura }: { peso?: string; altura?: string }): number =>
  (peso && altura && calcularIMC(parseNumber(peso), parseNumber(altura))) || undefined

const getIMC = ({ peso, altura }: { peso?: string; altura?: string }): number =>
  (peso && altura && calcularIMC(Number(peso), Number(altura))) || undefined
